/**
 * The client's half of the tenancy API (PRD §4.3, §4.4): query keys, fetchers and mutations.
 *
 * Every mutation invalidates through `touch()` rather than picking keys by hand, because the
 * three lists overlap — adding a person changes the Employees page *and* the project's headcount
 * *and* the company's, and forgetting one is how a UI starts lying.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AssignmentCreateInput,
  AssignmentPatchInput,
  CompanyDetail,
  CompanySummary,
  EmployeeCreateInput,
  EmployeePatchInput,
  ImportPreview,
  ImportResult,
  ImportRow,
  PersonSummary,
  ProjectCreateInput,
  ProjectSummary,
} from "@/shared/schemas/tenancy";
import { RD_SHARE_LIMITS_DEFAULT, type RdShareLimits } from "@/shared/rd-share-limits";
import { WORK_PROFILE_LIMITS_DEFAULT, type WorkProfileLimits } from "@/shared/work-profile-limits";

export const tenancyKeys = {
  companies: (archived = false) => ["companies", { archived }] as const,
  company: (companyId: string) => ["companies", companyId] as const,
  projects: (filter: { companyId?: string; archived?: boolean } = {}) =>
    ["projects", filter] as const,
  project: (projectId: string) => ["projects", projectId] as const,
  employees: (companyId: string, archived = false) =>
    ["employees", companyId, { archived }] as const,
  workProfileLimits: () => ["work-profile-limits"] as const,
  rdShareLimits: () => ["rd-share-limits"] as const,
};

function touch(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: ["companies"] });
  void client.invalidateQueries({ queryKey: ["projects"] });
  void client.invalidateQueries({ queryKey: ["employees"] });
}

/** PATCH/POST person payloads must land in the list cache before refetch, or a quick re-open of
 * Edit Stammdaten initializes from the stale row (empty jobDescription after save). */
function mergeEmployee(client: QueryClient, person: PersonSummary): void {
  client.setQueriesData<{ people: PersonSummary[] }>({ queryKey: ["employees"] }, (current) => {
    if (!current?.people) return current;
    return {
      ...current,
      people: current.people.map((row) => (row.id === person.id ? person : row)),
    };
  });
}

function query(params: Record<string, string | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === false) continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

/** Client-side Show-archived filter — keeps the list mounted when the checkbox flips. */
export function filterArchived<T extends { archived: boolean }>(
  items: readonly T[],
  showArchived: boolean,
): T[] {
  return showArchived ? [...items] : items.filter((item) => !item.archived);
}

// --------------------------------------------------------------------- reads

export function useWorkProfileLimits() {
  return useQuery({
    queryKey: tenancyKeys.workProfileLimits(),
    queryFn: () => api.get<{ limits: WorkProfileLimits }>("/work-profile-limits"),
    select: (data) => data.limits,
    staleTime: 60_000,
    placeholderData: { limits: WORK_PROFILE_LIMITS_DEFAULT },
  });
}

export function useRdShareLimits() {
  return useQuery({
    queryKey: tenancyKeys.rdShareLimits(),
    queryFn: () => api.get<{ limits: RdShareLimits }>("/rd-share-limits"),
    select: (data) => data.limits,
    staleTime: 60_000,
    placeholderData: { limits: RD_SHARE_LIMITS_DEFAULT },
  });
}

export function useCompanies(options: { archived?: boolean; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: tenancyKeys.companies(options.archived),
    queryFn: () =>
      api.get<{ companies: CompanySummary[] }>(
        `/companies${query({ archived: options.archived })}`,
      ),
    // An employee has no company list at all — the route answers 404 (PRD §4.1).
    enabled: options.enabled ?? true,
    select: (data) => data.companies,
  });
}

export function useCompany(companyId: string | undefined) {
  return useQuery({
    queryKey: tenancyKeys.company(companyId ?? "none"),
    queryFn: () => api.get<CompanyDetail>(`/companies/${companyId}`),
    enabled: Boolean(companyId),
  });
}

export function useProjects(
  options: { companyId?: string; archived?: boolean; enabled?: boolean } = {},
) {
  const { companyId, archived, enabled = true } = options;
  return useQuery({
    queryKey: tenancyKeys.projects({ companyId, archived }),
    queryFn: () =>
      api.get<{ projects: ProjectSummary[] }>(
        `/projects${query({ company: companyId, archived })}`,
      ),
    select: (data) => data.projects,
    enabled,
  });
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: tenancyKeys.project(projectId ?? "none"),
    queryFn: () =>
      api.get<{ project: ProjectSummary; hasTrackedData: boolean }>(`/projects/${projectId}`),
    enabled: Boolean(projectId),
  });
}

export function useEmployees(companyId: string | undefined, options: { archived?: boolean } = {}) {
  return useQuery({
    queryKey: tenancyKeys.employees(companyId ?? "none", options.archived),
    queryFn: () =>
      api.get<{ people: PersonSummary[] }>(
        `/companies/${companyId}/employees${query({ archived: options.archived })}`,
      ),
    enabled: Boolean(companyId),
    select: (data) => data.people,
  });
}

// --------------------------------------------------------------------- writes

function useTenancyMutation<TInput, TResult>(run: (input: TInput) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({ mutationFn: run, onSuccess: () => touch(client) });
}

export function useCreateCompany() {
  return useTenancyMutation((input: { name: string; bundesland: string }) =>
    api.post<{ company: CompanySummary }>("/companies", input),
  );
}

export function useUpdateCompany() {
  return useTenancyMutation((input: { id: string; patch: Record<string, unknown> }) =>
    api.patch<{ company: CompanySummary }>(`/companies/${input.id}`, input.patch),
  );
}

export type Lifecycle = "archive" | "restore" | "delete";

export function useCompanyLifecycle() {
  return useTenancyMutation(async (input: { id: string; action: Lifecycle }) => {
    if (input.action === "delete") return api.delete<void>(`/companies/${input.id}`);
    await api.post<{ company: CompanySummary }>(`/companies/${input.id}/${input.action}`);
  });
}

export function useCreateProject() {
  return useTenancyMutation((input: ProjectCreateInput) =>
    api.post<{ project: ProjectSummary }>("/projects", input),
  );
}

export function useUpdateProject() {
  return useTenancyMutation((input: { id: string; patch: Record<string, unknown> }) =>
    api.patch<{ project: ProjectSummary }>(`/projects/${input.id}`, input.patch),
  );
}

export function useProjectLifecycle() {
  return useTenancyMutation(async (input: { id: string; action: Lifecycle }) => {
    if (input.action === "delete") return api.delete<void>(`/projects/${input.id}`);
    await api.post<{ project: ProjectSummary }>(`/projects/${input.id}/${input.action}`);
  });
}

export function useAddEmployee(companyId: string) {
  return useTenancyMutation((input: EmployeeCreateInput) =>
    api.post<{ person: PersonSummary; invited: boolean }>(
      `/companies/${companyId}/employees`,
      input,
    ),
  );
}

export function useUpdateEmployee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: EmployeePatchInput }) =>
      api.patch<{ person: PersonSummary }>(`/users/${input.id}`, input.patch),
    onSuccess: (data) => {
      mergeEmployee(client, data.person);
      touch(client);
    },
  });
}

export function useChangeRole() {
  return useTenancyMutation((input: { id: string; role: "company_admin" | "employee" }) =>
    api.post<{ person: PersonSummary }>(`/users/${input.id}/role`, { role: input.role }),
  );
}

export function usePersonLifecycle() {
  return useTenancyMutation(async (input: { id: string; action: Lifecycle }) => {
    if (input.action === "delete") return api.delete<void>(`/users/${input.id}`);
    await api.post<{ person: PersonSummary }>(`/users/${input.id}/${input.action}`);
  });
}

export function useAssign() {
  return useTenancyMutation((input: { userId: string; assignment: AssignmentCreateInput }) =>
    // AssignmentCreateInput includes sendInvite (default true); dialog passes it through.
    api.post<{ person: PersonSummary }>(`/users/${input.userId}/assignments`, input.assignment),
  );
}

export function useUpdateAssignment() {
  return useTenancyMutation((input: { id: string; patch: AssignmentPatchInput }) =>
    api.patch<{ person: PersonSummary }>(`/assignments/${input.id}`, input.patch),
  );
}

export function useUnassign() {
  return useTenancyMutation((input: { id: string }) =>
    api.delete<{ removed: "deleted" | "archived" }>(`/assignments/${input.id}`),
  );
}

export function useInvitePeople(companyId: string) {
  return useTenancyMutation((input: { userIds: string[] }) =>
    api.post<{ results: { userId: string; email: string; status: string; reason?: string }[] }>(
      `/companies/${companyId}/employees/invite`,
      input,
    ),
  );
}

export function usePreviewImport(projectId: string) {
  return useMutation({
    mutationFn: (input: { file: File; entryDate: string }) =>
      api.upload<ImportPreview>(
        `/projects/${projectId}/employees/import/preview${query({
          filename: input.file.name,
          entryDate: input.entryDate,
        })}`,
        input.file,
      ),
  });
}

export function useCommitImport(projectId: string) {
  return useTenancyMutation(
    (input: { filename: string; defaultEntryDate: string; rows: ImportRow[] }) =>
      api.post<ImportResult>(`/projects/${projectId}/employees/import/commit`, input),
  );
}
